import { type VisitSource, visitSourceSchema } from "@rosc/contracts";
import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useRef, useState } from "react";
import { referenceClient } from "@/lib/orpc/client";
import { useVisitScope } from "@/lib/visit-queries";
import { visitSourcesKey } from "../sources/useVisitSources";

// Pre-chart drafts (clinical lead, 05/11/2026): a scan, photo or pasted
// screenshot is held by fast attach and read only when it is used, so its
// kind is not known when it is attached. Each one offered for adding is
// read now (readVisitSource), so a lab report gets the lab choice with its
// values, like a PDF with a text layer. While a file is read it is in
// `reading`; one that cannot be read is offered as before.
export const usePreChartReads = (
  visitPublicId: string | null | undefined,
  offered: readonly VisitSource[],
): ReadonlySet<number> => {
  const scope = useVisitScope();
  const client = useQueryClient();
  const started = useRef(new Set<number>());
  const [reading, setReading] = useState<ReadonlySet<number>>(() => new Set());
  const held = offered
    .filter((source) => source.readOnUse)
    .map((source) => source.id);
  const heldKey = held.join(",");
  // biome-ignore lint/correctness/useExhaustiveDependencies: keyed by the held ids
  useEffect(() => {
    if (!visitPublicId || !scope) return;
    for (const id of held) {
      if (started.current.has(id)) continue;
      started.current.add(id);
      setReading((ids) => new Set([...ids, id]));
      const key = visitSourcesKey(scope, visitPublicId);
      void (async () => {
        try {
          const source = visitSourceSchema.parse(
            await referenceClient.readVisitSource(
              {
                params: { public_id: visitPublicId, sourceId: String(id) },
                query: {},
              },
              { context: { expectedUserUid: scope.uid } },
            ),
          );
          if (source.visitPublicId === visitPublicId)
            client.setQueryData<VisitSource[]>(key, (sources = []) =>
              sources.map((item) => (item.id === source.id ? source : item)),
            );
        } catch {
          // Not read now: offered as before (read when it is used).
        } finally {
          setReading((ids) => new Set([...ids].filter((item) => item !== id)));
        }
      })();
    }
  }, [heldKey, visitPublicId, scope]);
  return reading;
};
